// Cruza el prospecto crudo con el catálogo y con la cartera para armar el renglón que se pinta.
//
// Las dos lecturas que enriquecen son BEST-EFFORT y por eso se resuelven aquí y no en la tabla: si
// el catálogo no cargó o el vendedor no puede leer la cartera, el renglón igual sale completo con
// lo que el propio prospecto trae.
import {
    isProspectOpen,
    prospectBucket,
    prospectName,
    type ProspectBucket,
} from '@/domains/sales/wizard/prospects';
import type { CatalogItem, Prospect } from '@/domains/sales/wizard/types';
import { shortFolio } from './prospectsFormat';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';

/**
 * TD-F2-WIZ-001: el silo ya publica el nombre del cliente en `GET /prospects` y `@fiado/type-kit`
 * todavía no lo declara, así que el campo se espeja aquí hasta que la lib lo alcance.
 */
export interface ProspectWithCustomerName extends Prospect {
    customerName?: string | null;
}

export interface ProspectRow {
    prospect: Prospect;
    /** El folio con nombre propio: la regla anti-UUID no deja pintar un campo `*Id` en el JSX. */
    folio: string;
    customerName: string;
    /** Marca del catálogo, la que alimenta el filtro de producto. `null` si el SKU no cruzó. */
    brand: string | null;
    /** «Marca Modelo», el SKU crudo si el catálogo no lo tiene, o que todavía no eligió. */
    productLabel: string;
    /** Tramo de la agenda. `null` en CLOSED y LOST: un prospecto resuelto ya no se agenda. */
    bucket: ProspectBucket | null;
}

export const NO_PRODUCT_LABEL = 'Sin especificar';

/** Lo que se lee cuando no hay nombre por ningún lado. El folio distingue una ficha de otra. */
export const UNNAMED_CUSTOMER_PREFIX = 'Cliente';

export interface ProspectRowSources {
    catalog: CatalogItem[];
    /** `retailCustomerId` → nombre, de la cartera de la tienda. Vacío si no se pudo leer. */
    customerNames: Map<string, string>;
    /** Día de calendario del piso de venta. Vacío mientras el render todavía no lo tiene. */
    today: string;
}

export function buildProspectRows(
    prospects: ProspectWithCustomerName[],
    { catalog, customerNames, today }: ProspectRowSources,
): ProspectRow[] {
    const bySku = new Map(catalog.map((item) => [item.sku, item]));
    return prospects.map((prospect) => {
        const product = prospect.productSku ? bySku.get(prospect.productSku) : undefined;
        return {
            prospect,
            folio: prospect.prospectId,
            customerName: resolveName(prospect, customerNames),
            brand: product?.brand || null,
            productLabel: product
                ? itemDisplayName(product)
                : (prospect.productSku ?? NO_PRODUCT_LABEL),
            bucket: today && isProspectOpen(prospect) ? prospectBucket(prospect, today) : null,
        };
    });
}

/**
 * Por orden de frescura: la cartera es una lectura viva, el del silo es la foto del momento en que
 * se vinculó, y el capturado a mano vive en el propio prospecto.
 */
function resolveName(
    prospect: ProspectWithCustomerName,
    customerNames: Map<string, string>,
): string {
    const known = prospect.retailCustomerId
        ? customerNames.get(prospect.retailCustomerId)
        : undefined;
    const denormalized = prospect.customerName?.trim();
    if (known) return known;
    if (denormalized) return denormalized;
    // Sin backfill, la ficha vieja de cliente identificado nunca va a tener nombre: se identifica
    // por su folio en lugar de por una etiqueta que comparte con todas las demás.
    if (!prospect.newCustomer) {
        return `${UNNAMED_CUSTOMER_PREFIX} ${shortFolio(prospect.prospectId)}`;
    }
    return prospectName(prospect);
}
